import { Col, Radio, Row, message } from 'antd';
import { Button } from '../../../../../design-system';
import { useAppContext } from '../../../../../providers/AppContext';
import { useEffect, useRef, useState } from 'react';
import { useSelected } from '../../../../../providers/selectedContext';
import KrTable from './table';
import KrChart from './chart';
import { calculateMean } from './meanValue';
import NoNwTable from './noNwTable';
import { NoNwTableData } from '../../../../../@types/kr/types';
import { calculateSample, updateSample } from '../../../../../api/data';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import { Accordion } from '../../../../../design-system/accordion';
import { CalculateSamplePayload, UpdateSamplePayload } from '../../../../../@types/samples/types';

const EMPTY_INDEXES: number[] = [];
type ExponentSource = 'automatic' | 'refinement';

interface SavedSelection {
  no: number[];
  nw: number[];
}

interface SavedMeans {
  no: number | null;
  nw: number | null;
}

interface TableData {
  Son: number | null;
  Swn: number | null;
  key: number;
}

const formatKrValue = (value: number | null) =>
  value == null ? '-' : value.toFixed(4);

const tableColumns = [
  {
    title: 'Son',
    dataIndex: 'Son',
    key: 'Son',
    render: (value: number | null) => formatKrValue(value)
  },
  {
    title: 'Swn',
    dataIndex: 'Swn',
    key: 'Swn',
    render: (value: number | null) => formatKrValue(value)
  }
];

const WaterOilKrValues = () => {
  const { updateConfigStates, data, updateData } = useAppContext();
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { selectedInfo, setSelectedInfo } = useSelected();
  const krAnalysis = selectedInfo?.sample?.k_rel_curves?.water_oil;
  const inputCurves = selectedInfo?.sample?.input_sample_curves?.water_oil;
  const [sw, setSw] = useState<{
    no_w: { value: number; display: boolean }[];
    nw: { value: number; display: boolean }[];
  }>({
    no_w: [{ value: 0, display: true }],
    nw: [{ value: 0, display: true }]
  });
  const [no_w, setNo] = useState<{ value: number; display: boolean }[]>([
    { value: 0, display: true }
  ]);
  const [nw, setNw] = useState<{ value: number; display: boolean }[]>([
    { value: 0, display: true }
  ]);
  const [noMean, setNoMean] = useState<number | null>();
  const [nwMean, setNwMean] = useState<number | null>();
  const [tableData, setTableData] = useState<TableData[]>([]);
  const [noTableData, setNoTableData] = useState<NoNwTableData[]>([]);
  const [tableOption, setTableOption] = useState<string>('no_w');
  const [exponentSource, setExponentSource] = useState<ExponentSource>('automatic');
  const [loading, setLoading] = useState<boolean>(false);
  const [resetPending, setResetPending] = useState<boolean>(false);
  const [modeSelections, setModeSelections] = useState<
    Partial<Record<ExponentSource, SavedSelection>>
  >({});
  const [modeSavedMeans, setModeSavedMeans] = useState<
    Partial<Record<ExponentSource, SavedMeans>>
  >({});
  const lastSampleUidRef = useRef<string | undefined>();

  const [savedNoMean, setSavedNoMean] = useState<number | null>(null);
  const [savedNwMean, setSavedNwMean] = useState<number | null>(null);
  const sampleUid = selectedInfo?.sample?.uid;
  const automaticNoIndexes =
    selectedInfo?.sample?.k_rel_curves?.water_oil?.indexes_no ??
    selectedInfo?.sample?.refinement_curves?.water_oil?.indexes_no;
  const automaticNwIndexes =
    selectedInfo?.sample?.k_rel_curves?.water_oil?.indexes_nw ??
    selectedInfo?.sample?.refinement_curves?.water_oil?.indexes_nw;
  const noIndexes =
    automaticNoIndexes ?? EMPTY_INDEXES;
  const nwIndexes =
    automaticNwIndexes ?? EMPTY_INDEXES;
  const hasAutomaticNoIndexes =
    Array.isArray(automaticNoIndexes) && automaticNoIndexes.length > 0;
  const hasAutomaticNwIndexes =
    Array.isArray(automaticNwIndexes) && automaticNwIndexes.length > 0;
  const persistedRefinedNoIndexes =
    selectedInfo?.sample?.regression_indexes?.refined_no_w_selected_indexes;
  const persistedRefinedNwIndexes =
    selectedInfo?.sample?.regression_indexes?.refined_nw_selected_indexes;
  const refinedNoIndexes =
    persistedRefinedNoIndexes ?? EMPTY_INDEXES;
  const refinedNwIndexes =
    persistedRefinedNwIndexes ?? EMPTY_INDEXES;
  const hasRefinedNoIndexes =
    Array.isArray(persistedRefinedNoIndexes) && persistedRefinedNoIndexes.length > 0;
  const hasRefinedNwIndexes =
    Array.isArray(persistedRefinedNwIndexes) && persistedRefinedNwIndexes.length > 0;

  useEffect(() => {
    if (lastSampleUidRef.current === sampleUid) {
      return;
    }

    lastSampleUidRef.current = sampleUid;
    setModeSelections({});
    setModeSavedMeans({});
  }, [sampleUid]);

  const calculateRefinedExponents = () => {
    const sample = selectedInfo?.sample;
    const waterOilCurves = sample?.input_sample_curves?.water_oil;
    const values = sample?.k_rel_values?.water_oil;
    const swValues = waterOilCurves?.sw_krel ?? [];
    const kroValues = waterOilCurves?.kro_lab ?? [];
    const krwValues = waterOilCurves?.krw_lab ?? [];
    const swi = sample?.input_sample_values?.swi ?? swValues[0] ?? null;
    const sor = values?.true_sor_w ?? values?.sor_w ?? null;
    const krwEndpoint = values?.true_krw ?? values?.krw_exponent ?? sample?.input_sample_values?.krw_absolute ?? null;

    if (swi == null || sor == null || krwEndpoint == null) {
      return {
        son: [],
        swn: [],
        noExponent: [],
        nwExponent: [],
      };
    }

    const denominator = 1 - swi - sor;
    if (denominator <= 0) {
      return {
        son: [],
        swn: [],
        noExponent: [],
        nwExponent: [],
      };
    }

    const son = swValues.map(value => (1 - value - sor) / denominator);
    const swn = swValues.map(value => (value - swi) / denominator);
    const noExponent = kroValues.map((kro, index) => {
      const sonValue = son[index];
      if (kro == null || sonValue == null || kro <= 0 || sonValue <= 0 || sonValue === 1) {
        return null;
      }
      const exponent = Math.log(kro) / Math.log(sonValue);
      return Number.isFinite(exponent) && exponent >= 0 ? exponent : null;
    });
    const nwExponent = krwValues.map((krw, index) => {
      const swnValue = swn[index];
      if (
        krw == null ||
        swnValue == null ||
        krw <= 0 ||
        swnValue <= 0 ||
        swnValue === 1 ||
        krwEndpoint <= 0
      ) {
        return null;
      }
      const exponent = (Math.log(krwEndpoint) - Math.log(krw)) / (Math.log(1) - Math.log(swnValue));
      return Number.isFinite(exponent) && exponent >= 0 ? exponent : null;
    });

    return { son, swn, noExponent, nwExponent };
  };

  useEffect(() => {
    if (
      krAnalysis?.no_exponent &&
      krAnalysis?.nw_exponent &&
      inputCurves?.sw_krel
    ) {
      const refined = calculateRefinedExponents();
      const isRefinement = exponentSource === 'refinement';
      const noSource = isRefinement ? refined.noExponent : krAnalysis.no_exponent;
      const nwSource = isRefinement ? refined.nwExponent : krAnalysis.nw_exponent;
      const localSelection = modeSelections[exponentSource];
      const localSavedMeans = modeSavedMeans[exponentSource];
      const persistedNoIndexes = isRefinement ? refinedNoIndexes : noIndexes;
      const persistedNwIndexes = isRefinement ? refinedNwIndexes : nwIndexes;
      const hasPersistedNoIndexes = isRefinement
        ? hasRefinedNoIndexes
        : hasAutomaticNoIndexes;
      const hasPersistedNwIndexes = isRefinement
        ? hasRefinedNwIndexes
        : hasAutomaticNwIndexes;
      const defaultNoIndexes = localSelection?.no
        ?? (hasPersistedNoIndexes ? persistedNoIndexes : null)
        ?? noSource.map((value, index) => (value != null ? index : -1)).filter(index => index >= 0);
      const defaultNwIndexes = localSelection?.nw
        ?? (hasPersistedNwIndexes ? persistedNwIndexes : null)
        ?? nwSource.map((value, index) => (value != null ? index : -1)).filter(index => index >= 0);

      const updatedSwNo = inputCurves.sw_krel.map((value, index) => ({
        value: value === null ? 0 : value,
        display: defaultNoIndexes.includes(index)
      }));

      const updatedSwNw = inputCurves.sw_krel.map((value, index) => ({
        value: value === null ? 0 : value,
        display: defaultNwIndexes.includes(index)
      }));

      const updatedNo = noSource.map((value, index) => ({
        value: value === null ? 0 : value,
        display: defaultNoIndexes.includes(index)
      }));

      const updatedNw = nwSource.map((value, index) => ({
        value: value === null ? 0 : value,
        display: defaultNwIndexes.includes(index)
      }));

      setSw({ no_w: updatedSwNo, nw: updatedSwNw });
      setNo(updatedNo);
      setNw(updatedNw);

      const noMeanCalc = calculateMean(updatedNo.filter(item => item.display).map(item => item.value));
      setNoMean(noMeanCalc);

      const nwMeanCalc = calculateMean(updatedNw.filter(item => item.display).map(item => item.value));
      setNwMean(nwMeanCalc);
      setSavedNoMean(
        localSavedMeans?.no
        ?? (isRefinement
          ? selectedInfo?.sample?.k_rel_values?.water_oil?.true_no_w ?? null
          : selectedInfo?.sample?.k_rel_values?.water_oil?.no_w ?? null)
      );
      setSavedNwMean(
        localSavedMeans?.nw
        ?? (isRefinement
          ? selectedInfo?.sample?.k_rel_values?.water_oil?.true_nw ?? null
          : selectedInfo?.sample?.k_rel_values?.water_oil?.nw ?? null)
      );
    } else {
      setSw({ no_w: [], nw: [] });
      setNo([]);
      setNw([]);
      setNoMean(null);
      setNwMean(null);
      updateConfigStates({ showKrTable: false });
    }
  }, [
    krAnalysis,
    inputCurves,
    noIndexes,
    nwIndexes,
    refinedNoIndexes,
    refinedNwIndexes,
    hasAutomaticNoIndexes,
    hasAutomaticNwIndexes,
    hasRefinedNoIndexes,
    hasRefinedNwIndexes,
    modeSelections,
    modeSavedMeans,
    exponentSource,
    selectedInfo
  ]);

  const getTableData = () => {
    const kRelData = {
      kro_corrected:
        selectedInfo?.sample?.k_rel_curves?.water_oil?.kro_corrected || [],
      krw_corrected:
        selectedInfo?.sample?.k_rel_curves?.water_oil?.krw_corrected || [],
      son: selectedInfo?.sample?.k_rel_curves?.water_oil?.son || [],
      swn: selectedInfo?.sample?.k_rel_curves?.water_oil?.swn || [],
      no_exponent:
        selectedInfo?.sample?.k_rel_curves?.water_oil?.no_exponent || [],
      nw_exponent:
        selectedInfo?.sample?.k_rel_curves?.water_oil?.nw_exponent || []
    };

    const sw = inputCurves?.sw_krel || [];

    const currentTableData: TableData[] = [];
    for (let i = 0; i < kRelData.son.length; i++) {
      currentTableData.push({
        Son: kRelData.son[i],
        Swn: kRelData.swn[i],
        key: i
      });
    }

    const currentNoNwData: NoNwTableData[] = [];
    for (let i = 0; i < kRelData.no_exponent.length; i++) {
      currentNoNwData.push({
        sw: sw[i],
        no_w: kRelData.no_exponent[i],
        nw: kRelData.nw_exponent[i],
        key: i
      });
    }

    setTableData(currentTableData);
    setNoTableData(currentNoNwData);
    currentTableData.length > 0 && updateConfigStates({ showKrTable: true });
  };

  useEffect(() => {
    getTableData();
  }, [selectedInfo]);

  useEffect(() => {
    const currentNoMean = calculateMean(
      no_w.filter((_, index) => no_w[index].display).map(item => item.value)
    );
    setNoMean(currentNoMean);

    const currentNwMean = calculateMean(
      nw.filter((_, index) => nw[index].display).map(item => item.value)
    );
    setNwMean(currentNwMean);
  }, [sw, no_w, nw]);

  const getDisplayedIndexes = (items: { display: boolean }[]) => {
    return items.reduce((indexes, item, index) => {
      if (item.display) {
        indexes.push(index);
      }
      return indexes;
    }, [] as number[]);
  };

  const handleExponentSourceChange = (value: ExponentSource) => {
    const currentSelection = {
      no: getDisplayedIndexes(no_w),
      nw: getDisplayedIndexes(nw)
    };
    const currentSavedMeans = {
      no: savedNoMean,
      nw: savedNwMean
    };

    setModeSelections(prev => ({
      ...prev,
      [exponentSource]: currentSelection
    }));
    setModeSavedMeans(prev => ({
      ...prev,
      [exponentSource]: currentSavedMeans
    }));
    setExponentSource(value);
    setResetPending(false);
  };

  const handleCalculateRefinement = async () => {
    setLoading(true);

    const indexesNo = getDisplayedIndexes(no_w);
    const indexesNw = getDisplayedIndexes(nw);

    try {
      let response;
      if (exponentSource === 'refinement') {
        const existingIndexes = selectedInfo?.sample?.regression_indexes || {};
        const payload: UpdateSamplePayload = {
          sample_uid: selectedInfo?.sample?.uid || '',
          regression_indexes: resetPending
            ? {
                ...existingIndexes,
                refined_no_w_selected_indexes: [],
                refined_nw_selected_indexes: [],
              }
            : {
                ...existingIndexes,
                refined_no_w_selected_indexes: indexesNo,
                refined_nw_selected_indexes: indexesNw,
              },
          true_no_w: resetPending ? null : noMean ?? savedNoMean ?? null,
          true_nw: resetPending ? null : nwMean ?? savedNwMean ?? null,
        };
        response = await updateSample(payload);
      } else {
        const payload: CalculateSamplePayload = {
          sample_uid: selectedInfo?.sample?.uid || '',
          strategies_keys: ['water-oil'],
          input_data_map: {
            'water-oil': {
              type: 'WATER_OIL',
              input_no: noMean ?? savedNoMean,
              input_nw: nwMean ?? savedNwMean,
              indexes_no: indexesNo,
              indexes_nw: indexesNw,
            },
          },
        };
        response = await calculateSample(payload);
      }
      const newWellSamplesData = data.wellSamples.map(sample => {
        if (sample.uid === response.uid) {
          return response;
        }

        return sample;
      });
      updateData({ wellSamples: newWellSamplesData });
      setSelectedInfo({ sample: response });
      setModeSelections(prev => {
        if (resetPending && exponentSource === 'refinement') {
          const nextSelections = { ...prev };
          delete nextSelections.refinement;
          return nextSelections;
        }

        return {
          ...prev,
          [exponentSource]: {
            no: indexesNo,
            nw: indexesNw
          }
        };
      });
      setModeSavedMeans(prev => {
        if (resetPending && exponentSource === 'refinement') {
          const nextSavedMeans = { ...prev };
          delete nextSavedMeans.refinement;
          return nextSavedMeans;
        }

        return {
          ...prev,
          [exponentSource]: {
            no: resetPending ? null : noMean ?? savedNoMean ?? null,
            nw: resetPending ? null : nwMean ?? savedNwMean ?? null
          }
        };
      });
      setResetPending(false);
    } catch (error) {
      console.error('error', error);
      message.error('Error saving Water-Oil Corey exponent values.');
    }
    setLoading(false);
  };

  const handleReset = () => {
    const resetNo = no_w.map(item => ({ ...item, display: true }));
    const resetNw = nw.map(item => ({ ...item, display: true }));
    const resetSwNo = sw.no_w.map(item => ({ ...item, display: true }));
    const resetSwNw = sw.nw.map(item => ({ ...item, display: true }));
    setNo(resetNo);
    setNw(resetNw);
    setSw({ no_w: resetSwNo, nw: resetSwNw });
    setNoMean(calculateMean(resetNo.map(item => item.value)));
    setNwMean(calculateMean(resetNw.map(item => item.value)));
    setSavedNoMean(exponentSource === 'refinement' ? null : selectedInfo?.sample?.k_rel_values?.water_oil?.no_w ?? null);
    setSavedNwMean(exponentSource === 'refinement' ? null : selectedInfo?.sample?.k_rel_values?.water_oil?.nw ?? null);
    setModeSelections(prev => ({
      ...prev,
      [exponentSource]: {
        no: getDisplayedIndexes(resetNo),
        nw: getDisplayedIndexes(resetNw)
      }
    }));
    setModeSavedMeans(prev => ({
      ...prev,
      [exponentSource]: {
        no: exponentSource === 'refinement'
          ? null
          : selectedInfo?.sample?.k_rel_values?.water_oil?.no_w ?? null,
        nw: exponentSource === 'refinement'
          ? null
          : selectedInfo?.sample?.k_rel_values?.water_oil?.nw ?? null
      }
    }));
    setResetPending(exponentSource === 'refinement');
  };

  const handleTableOptionChange = (value: string) => {
    setTableOption(value);
  };

  return (
    <Accordion defaultOpen="kr-values-water-oil">
      <Accordion.Item
        id="kr-values-water-oil"
        header={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 'bold' }}>
            <span>No &amp; Nw values</span>
            <span style={{ color: colors.waterOil }}>Water-Oil</span>
          </div>
        }
      >
        {krAnalysis ? (
          <div style={{ padding: '0.5rem' }}>
            <Row>
              <Col span={6}>
                <KrTable
                  columns={tableColumns}
                  data={tableData}
                  bordered={true}
                />
              </Col>
              <Col span={8}>
                <div
                  style={{
                    display: 'flex',
                    marginBottom: '0.5rem',
                    marginLeft: '0.5rem',
                    justifyContent: 'space-between'
                  }}
                >
                  <Radio.Group
                    value={exponentSource}
                    onChange={e => handleExponentSourceChange(e.target.value)}
                    options={[
                      { label: 'Lab values', value: 'automatic' },
                      { label: 'Refinement', value: 'refinement' }
                    ]}
                    style={{
                      width: '100%',
                      backgroundColor: 'transparent !important',
                      marginBottom: '0.5rem'
                    }}
                    block
                    optionType="button"
                    buttonStyle="solid"
                    className={isDay ? 'day-radio' : 'night-radio'}
                  />
                  <Radio.Group
                    onChange={e => handleTableOptionChange(e.target.value)}
                    options={[
                      { label: 'No', value: 'no_w' },
                      { label: 'Nw', value: 'nw' }
                    ]}
                    defaultValue={'no_w'}
                    style={{
                      width: '100%',
                      backgroundColor: 'transparent !important'
                    }}
                    block
                    optionType="button"
                    buttonStyle="solid"
                    className={isDay ? 'day-radio' : 'night-radio'}
                  />
                </div>
                <div>
                  {tableOption === 'no_w' ? (
                    <NoNwTable
                      tableData={noTableData}
                      values={no_w}
                      setValues={setNo}
                      type="no_w"
                      sw={sw.no_w}
                      setSw={setSw}
                    />
                  ) : (
                    <NoNwTable
                      tableData={noTableData}
                      values={nw}
                      setValues={setNw}
                      type="nw"
                      sw={sw.nw}
                      setSw={setSw}
                    />
                  )}
                </div>
              </Col>
              <Col span={10}>
                <div>
                  <KrChart
                    sw={tableOption === 'no_w' ? sw.no_w : sw.nw}
                    values={tableOption === 'no_w' ? no_w : nw}
                    setValues={tableOption === 'no_w' ? setNo : setNw}
                    setSw={setSw}
                    mean={tableOption === 'no_w' ? noMean : nwMean}
                    savedMean={tableOption === 'no_w' ? savedNoMean : savedNwMean}
                    setMean={tableOption === 'no_w' ? setNoMean : setNwMean}
                    setSavedMean={tableOption === 'no_w' ? setSavedNoMean : setSavedNwMean}
                    type={tableOption === 'no_w' ? 'No' : 'Nw'}
                    color={tableOption === 'no_w' ? '#059e05' : '#3c85fa'}
                  />
                </div>
                <div style={{ marginTop: '0.5rem', marginLeft: '0.5rem' }}>
                  <Button
                    variant="primary"
                    fullWidth
                    onClick={handleCalculateRefinement}
                    loading={loading}
                  >
                    Save {exponentSource === 'refinement' ? 'Refined' : 'Lab'} Values
                  </Button>
                  <Button
                    variant="secondary"
                    fullWidth
                    onClick={handleReset}
                    disabled={loading}
                    style={{ marginTop: '0.5rem' }}
                  >
                    Reset
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        ) : (
          <div className="no-data-div">No relative permeability values computed yet. Click <strong>Calculate</strong> in the Laboratory Data section to run the Kr analysis.</div>
        )}
      </Accordion.Item>
    </Accordion>
  );
};

export default WaterOilKrValues;
